// Keeps track of which wave owns each of the gust grains' slots (gust-grains.ts). A slot holds
// one wave's simulation and its layer of marks.
//
// A wave keeps its slot while it's live. Ended waves free their slots before new waves get one,
// so a new wave can reuse a slot in the same frame. A new wave with no free slot waits. Every
// slot a wave takes is reported as started, so its layer gets cleared: a layer still holding
// another wave's marks would show every cell as reached at once.

export class GrainSlots {
  private readonly ids: (number | null)[];
  /** The slots the last update freed and started. Each update refills them. */
  readonly freed: number[] = [];
  readonly started: number[] = [];

  constructor(count: number) {
    this.ids = Array.from({ length: count }, () => null);
  }

  /** The slot of a live wave, or -1. */
  slotOf(id: number): number {
    return this.ids.indexOf(id);
  }

  /** Whether any slot has a wave. */
  get live(): boolean {
    for (const id of this.ids) if (id !== null) return true;
    return false;
  }

  /** Frees the slots of waves that ended, then gives each new live wave a free slot. */
  update(live: readonly { readonly id: number }[]): void {
    this.freed.length = 0;
    this.started.length = 0;
    for (let i = 0; i < this.ids.length; i++) {
      const id = this.ids[i];
      if (id === null || id === undefined || isListed(live, id)) continue;
      this.ids[i] = null;
      this.freed.push(i);
    }
    for (const wave of live) {
      if (this.ids.includes(wave.id)) continue;
      const free = this.ids.indexOf(null);
      if (free < 0) continue;
      this.ids[free] = wave.id;
      this.started.push(free);
    }
  }

  /** Frees every slot, so the next update starts each live wave over. */
  clear(): void {
    this.ids.fill(null);
  }
}

function isListed(live: readonly { readonly id: number }[], id: number): boolean {
  for (const wave of live) if (wave.id === id) return true;
  return false;
}
